import { Component } from '@theme/component';

/** @typedef {typeof globalThis} Window */

/**
 * Shared scheduler for line-clamp writes.
 *
 * A collection page mounts one <product-title> per card, so a 24-product grid
 * runs 24 of these. Measuring and writing inline styles inside each element's
 * own ResizeObserver callback interleaved layout reads with style writes 24
 * times over, forcing a synchronous re-layout between every pair. Worse, the
 * writes mutated the very element the observer watched, so each pass could
 * schedule the next one ("ResizeObserver loop completed with undelivered
 * notifications").
 *
 * Instead every instance queues itself here: all reads happen together, then
 * all writes happen together, in a single animation frame.
 *
 * @type {Set<ProductTitle>}
 */
const pending = new Set();

/** @type {number | null} */
let frame = null;

function flush() {
  frame = null;

  const work = [...pending];
  pending.clear();

  // Read phase - no style writes in this loop, so layout is computed once.
  const measurements = work.map((instance) => instance.measure());

  // Write phase - only elements whose clamp actually changed are touched.
  for (let i = 0; i < work.length; i++) {
    const maxLines = measurements[i];
    if (maxLines != null) work[i].apply(maxLines);
  }
}

/**
 * @param {ProductTitle} instance
 */
function schedule(instance) {
  pending.add(instance);
  if (frame != null) return;
  frame = requestAnimationFrame(flush);
}

/**
 * A component that handles title truncation with responsive behavior
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [text] - The text element to truncate (optional)
 *
 * @extends {Component<Refs>}
 */
class ProductTitle extends Component {
  /** @type {number | null} */
  #appliedLines = null;

  /** @type {boolean} */
  #baseStylesApplied = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initializeTruncation();
  }

  /**
   * Initialize the title truncation
   */
  #initializeTruncation() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => schedule(this));
      this.resizeObserver.observe(this);
    } else {
      /** @type {Window} */
      (window).addEventListener('resize', this.#handleResize);
    }

    schedule(this);
  }

  /**
   * Read the element's geometry and work out how many lines fit.
   * Pure measurement - must not write any styles.
   *
   * @returns {number | null} The line count, or null when it cannot be determined.
   */
  measure() {
    if (!this.isConnected) return null;

    const textElement = this.#textElement;
    if (!textElement?.textContent) return null;

    const containerHeight = this.clientHeight;
    if (!containerHeight) return null;

    const computedStyle = window.getComputedStyle(this);
    const lineHeight = parseFloat(computedStyle.lineHeight);

    // `line-height: normal` parses to NaN, which used to propagate all the way
    // to `webkitLineClamp = "NaN"` - an invalid value the browser drops, so the
    // title silently rendered unclamped. Fall back to the font size instead.
    const resolvedLineHeight = Number.isFinite(lineHeight)
      ? lineHeight
      : parseFloat(computedStyle.fontSize) * 1.2;

    if (!Number.isFinite(resolvedLineHeight) || resolvedLineHeight <= 0) return null;

    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    const availableHeight = containerHeight - paddingTop - paddingBottom;

    return Math.max(1, Math.floor(availableHeight / resolvedLineHeight));
  }

  /**
   * Write the clamp, skipping the DOM entirely when nothing changed.
   *
   * @param {number} maxLines
   */
  apply(maxLines) {
    const textElement = this.#textElement;
    if (!textElement) return;

    // The box properties never vary, so they are set once rather than on every
    // resize - repeating them re-dirtied layout and re-triggered the observer.
    if (!this.#baseStylesApplied) {
      textElement.style.display = '-webkit-box';
      textElement.style.webkitBoxOrient = 'vertical';
      textElement.style.overflow = 'hidden';
      textElement.style.textOverflow = 'ellipsis';
      this.#baseStylesApplied = true;
    }

    if (this.#appliedLines === maxLines) return;
    this.#appliedLines = maxLines;
    textElement.style.webkitLineClamp = String(maxLines);
  }

  /** @returns {HTMLElement | null} */
  get #textElement() {
    return this.refs.text || this.querySelector('.title-text') || this;
  }

  /**
   * Handle window resize events
   */
  #handleResize = () => schedule(this);

  disconnectedCallback() {
    super.disconnectedCallback();
    pending.delete(this);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Previously this passed a freshly-bound function, so it never matched the
    // listener that was registered and the handler stayed attached for the life
    // of the page. A class field keeps one stable reference.
    window.removeEventListener('resize', this.#handleResize);
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

export default ProductTitle;
